"use client";

import React, { memo, useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AgentChartDialogPayload } from "@/lib/agent/agent-chart-payload";
import { validateAgentChartInput } from "@/lib/agent/validate-chart-spec";
import { VybeAgentMark } from "./VybeAgentMark";
import { AgentChartCanvas } from "./AgentChartCanvas";
import { useAgentChartModel } from "./useAgentChartModel";

export interface AgentChartToolPart {
  type: "tool-agent_chart";
  toolCallId: string;
  state:
    | "input-streaming"
    | "input-available"
    | "output-available"
    | "output-error";
  input?: unknown;
  output?: unknown;
  errorText?: string;
}

function chartStateLabel(state: AgentChartToolPart["state"]): string {
  switch (state) {
    case "input-streaming":
      return "Loading…";
    case "input-available":
      return "Rendering…";
    case "output-available":
      return "Chart";
    case "output-error":
      return "Error";
    default:
      return state;
  }
}

interface AgentChartBubbleProps {
  part: AgentChartToolPart;
  onOpenInDialog?: (payload: AgentChartDialogPayload) => void;
}

function AgentChartBubbleImpl({ part, onOpenInDialog }: AgentChartBubbleProps) {
  const [expanded, setExpanded] = useState(true);
  const onToggle = useCallback(() => setExpanded((v) => !v), []);

  const validated = useMemo(() => {
    if (part.state !== "output-available") return null;
    return validateAgentChartInput(part.input);
  }, [part.input, part.state]);

  const spec = validated?.ok ? validated.spec : null;
  const validationError = validated && !validated.ok ? validated.error : null;

  const model = useAgentChartModel(spec);

  const title =
    spec?.title ??
    (part.input as { title?: string } | undefined)?.title ??
    "Chart";

  const badgeClass =
    part.state === "output-error"
      ? "bg-destructive/15 text-destructive border-destructive/30"
      : part.state === "output-available"
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25"
        : "bg-muted text-muted-foreground border-border";

  const canExpand =
    part.state === "output-available" &&
    model.hasRenderableChart &&
    spec != null &&
    onOpenInDialog != null;

  const onExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!spec || !onOpenInDialog) return;
      onOpenInDialog({ spec, title });
    },
    [spec, title, onOpenInDialog],
  );

  return (
    <div className="flex min-w-0 gap-2.5">
      <div className="mt-0.5 shrink-0">
        <VybeAgentMark size={14} tone="primary" />
      </div>
      <div className="flex-1 min-w-0 rounded-md border border-border bg-muted/20 overflow-hidden">
        <div className="flex w-full min-w-0 items-center gap-2 overflow-hidden px-2 py-1.5">
          <button
            type="button"
            onClick={onToggle}
            className="flex min-w-0 flex-1 items-center gap-2 text-left hover:opacity-80 transition-opacity"
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0 flex-1 truncate text-[10px] font-medium text-foreground">
              {title}
            </span>
          </button>
          {canExpand ? (
            <button
              type="button"
              onClick={onExpand}
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Open chart in dialog"
              title="Open chart in dialog"
            >
              <Maximize2 className="h-3 w-3" />
            </button>
          ) : null}
          <span
            className={cn(
              "text-[9px] uppercase tracking-wide shrink-0 rounded border px-1 py-px",
              badgeClass,
            )}
          >
            {chartStateLabel(part.state)}
          </span>
        </div>
        {expanded ? (
          <div className="border-t border-border bg-background/40">
            {part.state === "output-error" ? (
              <p className="text-[10px] text-destructive px-2 pb-2">
                {part.errorText}
              </p>
            ) : validationError ? (
              <p className="text-[10px] text-destructive px-2 pb-2">
                {validationError}
              </p>
            ) : part.state === "output-available" ? (
              <AgentChartCanvas model={model} />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const AgentChartBubble = memo(AgentChartBubbleImpl);
AgentChartBubble.displayName = "AgentChartBubble";
